import { Link, useLocation } from "wouter";
import { ThemeToggle } from "../theme-toggle";
import { site } from "@/data/site";

import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { useAdminSession } from "@/hooks/use-admin-session";

export function Navbar() {
  const [location] = useLocation();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleScrollToSection = (id: string, e: React.MouseEvent) => {
    if (location === "/") {
      e.preventDefault();
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  // The Admin link only appears once the server confirms an authenticated
  // session — it is never shown to the public.
  const { authed } = useAdminSession();

  const navLinks = [
    { href: "/work", label: "Work" },
    { href: "/#what-i-do", label: "What I Do", onClick: (e: React.MouseEvent) => handleScrollToSection("what-i-do", e) },
    { href: "/about", label: "About" },
    // { href: "/field-notes", label: "Field Notes" },
    { href: "/contact", label: "Contact" },
    ...(authed ? [{ href: "/admin", label: "Admin" }] : []),
  ];

  return (
    <header 
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b border-transparent",
        scrolled ? "bg-background/80 backdrop-blur-md border-border/50 py-3 shadow-sm" : "bg-transparent py-5"
      )}
    >
      <div className="container mx-auto px-6 flex items-center justify-between">
        <Link href="/" className="text-xl font-serif font-semibold tracking-tight text-foreground hover:opacity-80 transition-opacity" data-testid="link-home">
          Travis Johnson
        </Link>

        <div className="hidden md:flex items-center gap-6">
          <nav className="flex items-center gap-6 text-sm font-medium text-muted-foreground">
            {navLinks.map((link) => (
              <Link 
                key={link.href} 
                href={link.href}
                onClick={link.onClick}
                className={cn(
                  "hover:text-foreground transition-colors",
                  location === link.href && "text-foreground"
                )}
                data-testid={`link-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>
          
          <div className="flex items-center gap-2 border-l border-border pl-6">
            <a 
              href={site.linkedin} 
              target="_blank" 
              rel="noopener noreferrer"
              className="p-2 text-muted-foreground hover:text-foreground transition-colors rounded-full hover:bg-foreground/5"
              aria-label="LinkedIn"
              data-testid="link-linkedin-nav"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
            </a>
            <ThemeToggle />
            <Link href="/contact" data-testid="link-nav-cta">
              <Button size="sm" className="ml-2 rounded-full px-5 font-medium">
                Let's Talk
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}
